import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import './FixedEquipment.css';
import { toast } from 'react-toastify';
import CreateFixedEquipmentModal from './CreateFixedEquipmentModal';
import EditFixedEquipmentModal from './EditFixedEquipmentModal';

export default function FixedEquipment({ propertyId }) {
  const [equipment, setEquipment] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');


  const fetchEquipment = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/fixed-equipment`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch fixed equipment');
      }

      const data = await response.json();
      setEquipment(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (newEquipment) => {
    setEquipment(prev => [newEquipment, ...prev]);
  };

  const handleStatusUpdate = async (equipmentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/fixed-equipment/${equipmentId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const updatedEquipment = await response.json();
      setEquipment(equipment.map(item => 
        item.id === updatedEquipment.id ? updatedEquipment : item
      ));

      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message);
    }
  };

  const handleViewCertificate = (item) => {
    if (item.test_certificate_url) {
      let url = item.test_certificate_url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = getApiEndpoint(`/${url}`);
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('No certificate available for this equipment');
    }
  };

  const handleEditClick = (item) => {
    setEditingEquipment(item);
    setShowEditModal(true);
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/fixed-equipment/${equipmentId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete equipment');
      }

      setEquipment(equipment.filter(item => item.id !== equipmentId));
      toast.success('Equipment deleted successfully');
    } catch (error) {
      console.error('Error deleting equipment:', error);
      toast.error(error.message);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchEquipment();
    }
  }, [propertyId]);

  // Helper to close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (!event.target.closest('.filter-dropdown') && !event.target.closest('.filter-btn')) {
        setShowFilterDropdown(false);
      }
    }
    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;

  // Filter equipment based on search and filters
  const filteredEquipment = equipment.filter(item => {
    const matchesSearch = !searchQuery.trim() || 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.equipment_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.location_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === 'All' || item.type === typeFilter;
    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="fixed-equipment-section">
      <h1>Fixed Equipment</h1>
      
      <div className="search-controls">
        <div className="search-bar">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search equipment"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="table-buttons" style={{ position: 'relative' }}>
          <button className="filter-btn" onClick={() => setShowFilterDropdown(v => !v)}>
            <i className="fas fa-filter"></i>
            Filter
          </button>
          {showFilterDropdown && (
            <div className="filter-dropdown" style={{ position: 'absolute', top: '40px', right: 0, zIndex: 10, background: '#fff', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 16, minWidth: 200 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600 }}>Type</label>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: '100%', marginTop: 4 }}>
                  <option value="All">All</option>
                  <option value="Lift">Lift</option>
                  <option value="Escalator">Escalator</option>
                  <option value="Moving Walkway">Moving Walkway</option>
                  <option value="Crane">Crane</option>
                  <option value="Hoist">Hoist</option>
                  <option value="Platform Lift">Platform Lift</option>
                  <option value="Goods Lift">Goods Lift</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600 }}>Status</label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '100%', marginTop: 4 }}>
                  <option value="All">All</option>
                  <option value="Pass">Pass</option>
                  <option value="Fail">Fail</option>
                  <option value="Pending">Pending</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setShowFilterDropdown(false)}>Close</button>
            </div>
          )}
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Add Equipment
          </button>
        </div>
      </div>

      <div className="table-section">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Equipment ID</th>
                <th>Name</th>
                <th>Type</th>
                <th>Location</th>
                <th>Manufacturer</th>
                <th>Status</th>
                <th>Last Inspection</th>
                <th>Next Inspection</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEquipment.map((item) => (
                <tr key={item.id}>
                  <td>{item.equipment_id}</td>
                  <td>{item.name}</td>
                  <td>{item.type}</td>
                  <td>{item.location_description}</td>
                  <td>{item.manufacturer || '-'}</td>
                  <td>
                    <select
                      value={item.status}
                      onChange={(e) => handleStatusUpdate(item.id, e.target.value)}
                      className={`status-select ${item.status.toLowerCase()}`}
                    >
                      <option value="Unknown">Unknown</option>
                      <option value="Pass">Pass</option>
                      <option value="Fail">Fail</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </td>
                  <td>{item.last_inspection_date ? new Date(item.last_inspection_date).toLocaleDateString('en-GB') : '-'}</td>
                  <td>{item.next_inspection_date ? new Date(item.next_inspection_date).toLocaleDateString('en-GB') : '-'}</td>
                  <td style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {item.test_certificate_url && (
                      <button 
                        className="view-test-data-btn system-blue"
                        onClick={() => handleViewCertificate(item)}
                        title="View Certificate"
                      >
                        <i className="fas fa-file-alt"></i> Certificate
                      </button>
                    )}
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditClick(item)}
                      title="Edit Equipment"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteEquipment(item.id)}
                      title="Delete Equipment"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateFixedEquipmentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        propertyId={propertyId}
      />

      {showEditModal && editingEquipment && (
        <EditFixedEquipmentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          equipment={editingEquipment}
          onSuccess={updatedEquipment => {
            setEquipment(equipment.map(item => item.id === updatedEquipment.id ? updatedEquipment : item));
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
} 